import { useEffect, useState } from 'react';
import { Card, Button, Form, Row, Col, Toast, ToastContainer } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { useAppContext } from 'providers/AppProvider';
import { API_BASE } from 'apiConfig';

interface WidgetSettings {
  backgroundColor: string;
  textColor: string;
  widgetLocation: 'left' | 'right';
  minimizedChatTextOnline: string;
  minimizedChatTextOffline: string;
  greeting: string;
  inviteText: string;
  inviteEnabled: boolean;
  inviteDelay: number;
  preChatEnabled: boolean;
  preChatRequireName: boolean;
  preChatRequireEmail: boolean;
}

const DEFAULTS: WidgetSettings = {
  backgroundColor: '#111111',
  textColor: '#ffffff',
  widgetLocation: 'right',
  minimizedChatTextOnline: 'Chat with us',
  minimizedChatTextOffline: 'Leave us a message',
  greeting: 'Hi there! 👋 How can we help?',
  inviteText: 'Have a question? Chat with us!',
  inviteEnabled: true,
  inviteDelay: 8,
  preChatEnabled: false,
  preChatRequireName: false,
  preChatRequireEmail: true
};

const WidgetAppearance = () => {
  const token = useSelector((state: RootState) => state.auth.token);
  const {
    config: { isDark }
  } = useAppContext();

  const [s, setS] = useState<WidgetSettings>(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);

  useEffect(() => {
    fetch(`${API_BASE}/account/widget-settings`, {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined
    })
      .then(r => (r.ok ? r.json() : null))
      .then(d => {
        if (d?.widget_settings) setS({ ...DEFAULTS, ...d.widget_settings });
      })
      .catch(() => undefined)
      .finally(() => setLoading(false));
  }, [token]);

  const set = <K extends keyof WidgetSettings>(k: K, v: WidgetSettings[K]) =>
    setS(prev => ({ ...prev, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/account/widget-settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ widget_settings: s })
      });
      setToast(
        res.ok
          ? { msg: 'Widget settings saved.', ok: true }
          : { msg: 'Could not save. Please try again.', ok: false }
      );
    } catch {
      setToast({ msg: 'Could not save. Please try again.', ok: false });
    } finally {
      setSaving(false);
    }
  };

  const side = s.widgetLocation === 'left' ? { left: 20 } : { right: 20 };

  return (
    <>
      <h2 className="mb-1 text-body-emphasis">Widget appearance</h2>
      <p className="text-body-tertiary mb-4">
        Customize how your chat widget looks and what it says. Changes apply to your live widget.
      </p>

      <Row className="g-4">
        <Col xs={12} lg={7}>
          <Card>
            <Card.Body className="d-flex flex-column gap-4">
              {/* Colors */}
              <div>
                <h5 className="mb-3">Colors</h5>
                <Row className="g-3">
                  <Col xs={6}>
                    <Form.Label className="fs-9">Primary color</Form.Label>
                    <div className="d-flex align-items-center gap-2">
                      <Form.Control
                        type="color"
                        value={s.backgroundColor}
                        onChange={e => set('backgroundColor', e.target.value)}
                        style={{ width: 48, height: 38, padding: 2 }}
                      />
                      <Form.Control
                        value={s.backgroundColor}
                        onChange={e => set('backgroundColor', e.target.value)}
                      />
                    </div>
                  </Col>
                  <Col xs={6}>
                    <Form.Label className="fs-9">Text color</Form.Label>
                    <div className="d-flex align-items-center gap-2">
                      <Form.Control
                        type="color"
                        value={s.textColor}
                        onChange={e => set('textColor', e.target.value)}
                        style={{ width: 48, height: 38, padding: 2 }}
                      />
                      <Form.Control
                        value={s.textColor}
                        onChange={e => set('textColor', e.target.value)}
                      />
                    </div>
                  </Col>
                </Row>
              </div>

              {/* Position */}
              <div>
                <h5 className="mb-3">Position</h5>
                <div className="d-flex gap-4">
                  <Form.Check
                    type="radio"
                    label="Bottom right"
                    checked={s.widgetLocation === 'right'}
                    onChange={() => set('widgetLocation', 'right')}
                  />
                  <Form.Check
                    type="radio"
                    label="Bottom left"
                    checked={s.widgetLocation === 'left'}
                    onChange={() => set('widgetLocation', 'left')}
                  />
                </div>
              </div>

              {/* Content */}
              <div>
                <h5 className="mb-3">Content</h5>
                <Form.Group className="mb-3">
                  <Form.Label className="fs-9">Launcher text (online)</Form.Label>
                  <Form.Control
                    value={s.minimizedChatTextOnline}
                    onChange={e => set('minimizedChatTextOnline', e.target.value)}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label className="fs-9">Launcher text (away)</Form.Label>
                  <Form.Control
                    value={s.minimizedChatTextOffline}
                    onChange={e => set('minimizedChatTextOffline', e.target.value)}
                  />
                </Form.Group>
                <Form.Group className="mb-0">
                  <Form.Label className="fs-9">Greeting message</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={s.greeting}
                    onChange={e => set('greeting', e.target.value)}
                  />
                </Form.Group>
              </div>

              {/* Invite bubble */}
              <div>
                <h5 className="mb-3">Invitation bubble</h5>
                <Form.Check
                  type="switch"
                  className="mb-3"
                  label="Show an invitation bubble after a delay"
                  checked={s.inviteEnabled}
                  onChange={e => set('inviteEnabled', e.target.checked)}
                />
                {s.inviteEnabled && (
                  <Row className="g-3">
                    <Col xs={8}>
                      <Form.Label className="fs-9">Invitation text</Form.Label>
                      <Form.Control
                        value={s.inviteText}
                        onChange={e => set('inviteText', e.target.value)}
                      />
                    </Col>
                    <Col xs={4}>
                      <Form.Label className="fs-9">Delay (seconds)</Form.Label>
                      <Form.Control
                        type="number"
                        min={0}
                        max={600}
                        value={s.inviteDelay}
                        onChange={e => set('inviteDelay', Number(e.target.value))}
                      />
                    </Col>
                  </Row>
                )}
              </div>

              {/* Pre-chat form */}
              <div>
                <h5 className="mb-3">Before the chat starts</h5>
                <Form.Check
                  type="switch"
                  className="mb-2"
                  label="Ask the visitor for their details before chatting"
                  checked={s.preChatEnabled}
                  onChange={e => set('preChatEnabled', e.target.checked)}
                />
                {s.preChatEnabled && (
                  <div className="ms-3">
                    <Form.Check
                      type="checkbox"
                      label="Require name"
                      checked={s.preChatRequireName}
                      onChange={e => set('preChatRequireName', e.target.checked)}
                    />
                    <Form.Check
                      type="checkbox"
                      label="Require email"
                      checked={s.preChatRequireEmail}
                      onChange={e => set('preChatRequireEmail', e.target.checked)}
                    />
                  </div>
                )}
              </div>

              <div>
                <Button variant="primary" onClick={save} disabled={saving || loading}>
                  {saving ? 'Saving…' : 'Save changes'}
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Live preview */}
        <Col xs={12} lg={5}>
          <Card>
            <Card.Body>
              <h5 className="mb-3">Live preview</h5>
              <div
                style={{
                  position: 'relative',
                  height: 360,
                  borderRadius: 12,
                  overflow: 'hidden',
                  background: isDark ? '#15161a' : '#eef1f5',
                  border: '0.5px solid rgba(0,0,0,0.1)'
                }}
              >
                {/* Panel */}
                <div
                  style={{
                    position: 'absolute',
                    bottom: 84,
                    ...side,
                    width: 250,
                    borderRadius: 14,
                    overflow: 'hidden',
                    background: '#fff',
                    boxShadow: '0 8px 30px rgba(0,0,0,0.18)'
                  }}
                >
                  <div
                    style={{
                      background: s.backgroundColor,
                      color: s.textColor,
                      padding: '14px 16px'
                    }}
                  >
                    <div style={{ fontWeight: 600, fontSize: 15 }}>Chat with us</div>
                    <div style={{ fontSize: 11, opacity: 0.9 }}>● We are online</div>
                  </div>
                  <div style={{ padding: 14, background: '#f6f7f9', minHeight: 90 }}>
                    <div
                      style={{
                        background: '#fff',
                        border: '1px solid #eaecf0',
                        borderRadius: 12,
                        borderBottomLeftRadius: 4,
                        padding: '9px 12px',
                        fontSize: 13,
                        maxWidth: '85%'
                      }}
                    >
                      {s.greeting || ' '}
                    </div>
                  </div>
                </div>

                {/* Invite bubble */}
                {s.inviteEnabled && s.inviteText && (
                  <div
                    style={{
                      position: 'absolute',
                      bottom: 84,
                      ...side,
                      maxWidth: 220,
                      background: '#1f2229',
                      color: '#fff',
                      padding: '8px 12px',
                      borderRadius: 12,
                      fontSize: 12
                    }}
                  >
                    {s.inviteText}
                  </div>
                )}

                {/* Launcher */}
                <div
                  style={{
                    position: 'absolute',
                    bottom: 20,
                    ...side,
                    background: s.backgroundColor,
                    color: s.textColor,
                    borderRadius: 24,
                    padding: '11px 18px',
                    fontWeight: 600,
                    fontSize: 14,
                    boxShadow: '0 6px 20px rgba(0,0,0,0.25)'
                  }}
                >
                  {s.minimizedChatTextOnline}
                </div>
              </div>
              <p className="fs-9 text-body-tertiary mt-3 mb-0">
                Position changes take effect on the visitor's next page load.
              </p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <ToastContainer position="bottom-end" className="p-3">
        <Toast
          show={!!toast}
          onClose={() => setToast(null)}
          delay={2500}
          autohide
          bg={toast?.ok ? 'success' : 'danger'}
        >
          <Toast.Body className="text-white">{toast?.msg}</Toast.Body>
        </Toast>
      </ToastContainer>
    </>
  );
};

export default WidgetAppearance;
